import { t } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { Order } from '../../../../../types/apiResponse/order';
import { ColumnConfig } from '../../constants/defaultColumnsConfig';
import { useOrderMoneyDecimals } from '../../OrderMoneyPrecision';
import { formatOrderMoney, orderMoneyColumns } from '../../orderMoney';

type Props = {
  orders: Order[];
  columnsConfig: ColumnConfig[];
  fixed?: 'show' | 'hide';
  className?: string;
  selectedItems?: Order[];
};

const OrderTableData = ({
  orders,
  columnsConfig,
  fixed = 'show',
  className = '',
  selectedItems = [],
}: Props) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const decimals = useOrderMoneyDecimals();
  const { i18n } = useTranslation();
  const locale = (i18n.resolvedLanguage || i18n.language || 'pt').startsWith('en') ? 'en-US' : 'pt-BR';

  const renderColumnValue = (order: Order, columnKey: keyof Order) => {
    const value = order[columnKey];
    if (orderMoneyColumns.has(columnKey)) return formatOrderMoney(value, decimals, locale, '-');

    if (typeof value === 'string' || typeof value === 'number') {
      return value;
    }

    if (typeof value === 'boolean') {
      return value ? 'Sim' : 'Não';
    }

    if (value === null || value === undefined) {
      return '-';
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return '';
  };

  const isSelected = (order: Order) => {
    return selectedItems?.some((item) => item.NumeroPedido === order.NumeroPedido) || false;
  };

  if (orders.length === 0) {
    return null;
  }

  // Filter columns based on fixed prop
  const visibleColumns = columnsConfig.filter((col) => {
    if (!col.visible) return false;
    if (col.subItems.length > 0) return false;
    if (fixed === 'show') return col.fixed;
    if (fixed === 'hide') return !col.fixed;
    return true;
  });

  // Check if there are any columns to show
  if (visibleColumns.length === 0) {
    return null;
  }

  const sectionType = fixed === 'show' ? 'fixed-section' : 'scrollable-section';
  const sectionTitle = fixed === 'show' ? t('fixedColumns') : t('otherColumns');

  return (
    <div className={`orders-section ${sectionType} ${isCollapsed ? 'collapsed' : ''} ${className}`}>
      {/* Section Header */}
      <div className="section-header">
        <button
          className="section-toggle"
          onClick={() => setIsCollapsed(!isCollapsed)}
          title={isCollapsed ? t('expand') : t('collapse')}
        >
          {isCollapsed ? <MdChevronRight /> : <MdChevronLeft />}
        </button>
        <div className="section-title">
          {sectionTitle}
          <span className="section-badge">{visibleColumns.length}</span>
        </div>
      </div>

      {/* Section Content */}
      <div className="section-content">
        <div className={`section-columns${fixed === 'show' ? '-fixed' : ''}`}>
          {visibleColumns.map((columnConfig) => (
            <div className="column" key={columnConfig.key}>
              {/* Column Header */}
              <div className="column-header">{t(columnConfig.label) as string}</div>

              {/* Column Data */}
              {orders.map((order, index) => (
                <div
                  className={
                    `row ${index % 2 === 0 ? 'odd' : ''} ${isSelected(order) ? 'selected' : ''}`}
                  key={`${order.NumeroPedido}-${columnConfig.key}`}
                >
                  <div className="cell" title={String(renderColumnValue(order, columnConfig.key))}>
                    {renderColumnValue(order, columnConfig.key)}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OrderTableData;
